'use strict';

var AudioDspDevice = require('./util/AudioDspDevice');
var CallsDevice = require('./util/CallsDevice');
var CmdDevice = require('./util/CmdDevice');
var log = require('../../../log/Log').channel('ControlSpace');

var allowedTypes = {
    amplifier: [ 'crr', 'gain' ],
    assistiveListeningMute: [ 'gain' ],
    assistiveListeningVolume: [ 'gain' ],
    audioInputMute: [ 'gain' ],
    audioInputVolume: [ 'gain' ],
    mics: [ 'crr', 'gain' ],
    overflowRoomMute: [ 'gain' ],
    overflowRoomVolume: [ 'gain' ],
    receiveGain: [ 'gain', 'crr' ],
    soundReinforcementMute: [ 'gain' ],
    soundReinforcementVolume: [ 'gain' ],
    telephony: [ 'pstn', 'voip' ],
};

var indices = {
    crr: {
        volume: [ '1', '1' ],
        volumeMute: [ '1', '2' ],
        micsMute: [ '1', '4' ],
        telReceiveGain: function(index) { // index = 1 - 8
            return [ '2', (2 * (index - 1) + 3).toString() ];
        },
    },
    gain: {
        level: [ '1' ],
        mute: [ '2' ],
    },
    tel: {
        accountStatus: [ '0', '0' ], // only on voip
        callStatus: [ '0', '1' ],
        callerId: [ '0', '2' ],
        dialKey: [ '1' ],
        call: [ '2' ],
        endCall: [ '3' ],
        answer: [ '4' ],
    },
};

var ranges = {
    crr: {
        volume: { min: -38, max: 12 },
    },
    gain: {
        volume: { min: -38, max: 12 },
        trim: { min: -12, max: 12 },
    },
};

// Valid GA indices by module type
//
// crr: 0, 1-9; 1, 1-9 (docs say 1-9, but 9 returns error 99)
// gain 1-2
// pstn input: 0, 1-9; 1, 1-2
// voip input: 0, 0-7; 1, 1-2
//
// The validation indices below were selected to uniquely determine the type
// of module as long as they are queried in order.

var validationTypes = [
    { type: 'crr', indices: [ 1, 8 ] },
    { type: 'gain', indices: [ 2 ] },
    { type: 'voip', indices: [ 0, 0 ] },
    { type: 'pstn', indices: [ 0, 9 ] },
];

function getFilter(channel, filter) {
    if (allowedTypes.hasOwnProperty(filter)) {
        return allowedTypes[filter].includes(channel.type);
    }

    return false;
}

function makeSimulationDetectedChannels() {
    var detected = {};
    var channels = [
        { module: 'CRR 1', type: 'crr' },
        { module: 'Overflow Room A', type: 'gain' },
        { module: 'Overflow Room B', type: 'gain' },
        { module: 'Assistive Listening', type: 'gain' },
        { module: 'Audio Input A', type: 'gain' },
        { module: 'Audio Input B', type: 'gain' },
        { module: 'Audio Input C', type: 'gain' },
        { module: 'VoIP In 1', type: 'voip' },
    ];

    channels.forEach(function(chan, i) {
        detected['a' + i] =  Object.assign({ filters: {} }, chan);
        Object.keys(allowedTypes).forEach(function(filter) {
            detected['a' + i].filters[filter] = getFilter(chan, filter);
        });
    });

    return detected;
}

// During validation, we build up a list of "channels" in this.channels
// corresponding to the modules referenced in our config. Each channel has
// these properties.
// - module: the name of the module (string)
// - type: 'crr', 'gain', 'pstn', 'voip'
// - ranges: (defined if appropriate)
//     - trim: { min: xx, max: yy }, (defined if appropriate)
//     - volume: { min: xx, max yy }, (defined if appropriate)

// The call interface objects in the CallsDevice mixin will receive the
// following properties from the config:
// - configId
// - input
// - receiveGain (optional)
//   - module
//   - index (optional)
// - receiveGainValue
// We also augment the call interface objects with the following properties:
// - callStatusValue
// - receiveGainIndices
// - callerId

function ControlSpace(ipAddress, username, password, onConnectStatus, onControlStatus, config) {
    this.channels = [];

    AudioDspDevice.call(this);
    CallsDevice.call(this);
    CmdDevice.call(this, ipAddress, username, password, onConnectStatus, onControlStatus, config);
}

ControlSpace.prototype = Object.create(CmdDevice.prototype);
ControlSpace.prototype.constructor = ControlSpace;

Object.assign(ControlSpace.prototype, AudioDspDevice.prototype);
Object.assign(ControlSpace.prototype, CallsDevice.prototype);

ControlSpace.prototype.name = 'ControlSpace';
ControlSpace.prototype.key = '-KadGj3uu6fntaq0RG_r';
ControlSpace.prototype.configName = 'controlSpaceControlChannels';
ControlSpace.prototype.failOnEmptyConfig = true;
ControlSpace.prototype.isAsync = false;
ControlSpace.prototype.syncTimeoutMs = 2000;
ControlSpace.prototype.rejectAsync = false;
ControlSpace.prototype.receiveTerminators = [ '\r', ';' ];
ControlSpace.prototype.unterminatedRegExp = /\x15../; // NAKs are not terminated
ControlSpace.prototype.pollIntervalMs = 750;

ControlSpace.prototype.ports = [{
    port: 10055,
    verifySend: 'NP T',
    verifyConnection: /^\x06*NP T,.;*$/,
    verifyTimeoutMs: 2500,
    loginSkip: null,
    userPrompt: null,
    userPromptTimeoutMs: 0,
    userSend: null,
    userPromptHasNewline: false,
    passwordPrompt: null,
    passwordPromptTimeoutMs: 0,
    passwordPromptHasNewline: false,
    passwordDefault: null,
    passwordSend: null,
    loginVerify: null,
    loginReject: null,
    loginVerifyTimeoutMs: 0,
}];


ControlSpace.prototype.simulationParams = {
    caps: {
        hasAssistiveListening: true,
        hasAudioInputSourcesMute: true,
        hasRoomMute: true,
        hasRoomVolume: true,
        hasRoomVolumeMute: true,
        hasOverflowRoomsMute: true,
        hasSoundReinforcement: true,
        maxCalls: {
            pstn: 0,
            video: 0,
            voip: 1,
        },
    },
    callHasReceiveGain: function(ci, config, detected) {
        if (config.hasOwnProperty('telephony') &&
            config.telephony.hasOwnProperty(ci.configId) &&
            config.telephony[ci.configId].hasOwnProperty('receiveGain') &&
            config.telephony[ci.configId].receiveGain.module === 'CRR 1' &&
            config.telephony[ci.configId].receiveGain.hasOwnProperty('index') &&
            config.telephony[ci.configId].receiveGain.index === 1) {
            return true;
        }

        return false;
    },
    detected: makeSimulationDetectedChannels(),
    // firmwareVersion: not supported by ControlSpace
    channelsAreEqual: function(a, b) {
        if (typeof a === 'string') {
            return a === b;
        } else {
            if (a.hasOwnProperty('index')) {
                return a.module === b.module &&
                    b.hasOwnProperty('index') &&
                    a.index === b.index;
            } else {
                return a.module === b.module &&
                    !b.hasOwnProperty('index');
            }
        }
    },
    channelMatchesDetected: function(chan, det) {
        if (typeof chan === 'string') {
            return chan === det.module;
        } else {
            return chan.module === det.module;
        }
    },
    getTelephonyType: function(cfg, detected) {
        return 'voip';
    },
    telephonyChannels: [
        { name: 'input', filter: 'telephony' },
        { name: 'receiveGain', filter: 'receiveGain' },
    ],
};

ControlSpace.prototype.commands = {};

ControlSpace.prototype.commands.$ga = {};
ControlSpace.prototype.commands.$ga.isAsync = true;
ControlSpace.prototype.commands.$ga.activeStates = [ 'validating', 'controlling' ];
ControlSpace.prototype.commands.$ga.activeValidateStates = [ 'gettingValues' ];
ControlSpace.prototype.commands.$ga.match = /GA"(.+)">(\d+)(?:>(\d+))?=(")?(.*)\4/;
ControlSpace.prototype.commands.$ga.process = function(match) {
    var name = match[1];
    var index0 = match[2];
    var index1 = match[3];
    var value = match[5];
    var chan = this.getChannel(name);

    if (typeof chan === 'undefined') return;

    if (chan.type === 'crr' || chan.type === 'gain') {
        var cmdName = null;
        var ctrlType = null;
        var devValue;

        if (chan.type === 'crr') {
            if (this.indicesMatch(index0, index1, indices.crr.volume)) {
                cmdName = 'volume';
                ctrlType = 'volume';
            } else if (this.indicesMatch(index0, index1, indices.crr.volumeMute)) {
                cmdName = 'volumeMute';
                ctrlType = 'mute';
            } else if (this.indicesMatch(index0, index1, indices.crr.micsMute)) {
                cmdName = 'mute';
                ctrlType = 'mute';
            }
        } else { // chan.type === 'gain'
            if (this.indicesMatch(index0, index1, indices.gain.level)) {
                ctrlType = 'volume';
            } else if (this.indicesMatch(index0, index1, indices.gain.mute)) {
                ctrlType = 'mute';
            }
        }

        if (ctrlType === 'volume') {
            devValue = parseFloat(value);
        } else { // ctrlType === 'mute'
            devValue = value === 'O'; // 'O' for on, 'F' for off
        }

        if (ctrlType !== null) {
            var ctrl = this.doAudioUpdate(devValue, function(ctrl, cfg) {
                if (cmdName === null) return ctrl.type === ctrlType && cfg === name;
                return cmdName === ctrl.cmdName && cfg === name;
            }, this.getRange.bind(this));

            if (ctrl && ctrl.cmdName === 'volume') {
                var newVolumeMute = devValue <= ranges.crr.volume.min;

                if (newVolumeMute !== this.volumeMute) {
                    var volumeMuteCtrl = this.getAudioControl('volumeMute');
                    this.volumeMute = newVolumeMute;

                    if (this.fsm.current === 'controlling') {
                        this.broadcastStatus('volumeMute', this.volumeMute);
                    }

                    this.sendMute(volumeMuteCtrl, this.config.amplifier, this.volumeMute);
                }
            }

            if (!ctrl) { // it wasn't handled, so maybe it is receiveGain
                for (var i = 0; i < this.callInterfaces.length; i++) {
                    var ci = this.callInterfaces[i];
                    if (this.getNode(ci, 'receiveGain.module') !== name) continue;
                    if (!ci.hasOwnProperty('receiveGainIndices')) continue;
                    if (this.indicesMatch(index0, index1, ci.receiveGainIndices)) {
                        // don't convert value since device and system ranges match (-10, 10)
                        this.updateReceiveGain(ci, value);
                        break;
                    }
                }
            }
        }
    } else { // 'pstn' or 'voip'
        if (this.indicesMatch(index0, index1, indices.tel.callStatus)) {
            this.handleCallStatus(chan, value);
        } else if (this.indicesMatch(index0, index1, indices.tel.callerId)) {
            this.handleCallerId(chan, value);
        }
    }

    if (this.fsm.current === 'validating') {
        var query = this.valuesToQuery[this.validateRepeatIndex];

        if (query.name === name && this.indicesMatch(index0, index1, query.indices)) {
            this.doNextValidateStep();
        }
    }
};

ControlSpace.prototype.commands.$validateChannel = {};
ControlSpace.prototype.commands.$validateChannel.activeStates = 'validating';
ControlSpace.prototype.commands.$validateChannel.activeValidateStates = [ 'gettingChannels' ];
ControlSpace.prototype.commands.$validateChannel.get = function() {
    var module = this.channelsToValidate[this.validateRepeatIndex];
    var vt = validationTypes[this.validationTypeIndex];
    this.sendModuleCommand('$validateChannel', 'GA', module, vt.indices);
};
ControlSpace.prototype.commands.$validateChannel.match = /\x15(..)|GA"(.+)">(\d+)(?:>(\d+))?=(.+)/;
ControlSpace.prototype.commands.$validateChannel.process = function(match) {
    var module = this.channelsToValidate[this.validateRepeatIndex];
    var nack = typeof match[1] !== 'undefined';
    var errorCode = match[1];
    var name = match[2];
    // var index0 = match[3];
    // var index1 = match[4];
    // var value = match[5];

    if (nack) {
        if (errorCode === '01') { // invalid module name
            this.defer(this.doNextValidateStep.bind(this));
        } else { // invalid indices, so try the next type
            this.validationTypeIndex++;

            if (this.validationTypeIndex >= validationTypes.length) {
                this.defer(this.doNextValidateStep.bind(this));
            } else {
                this.query('$validateChannel');
            }
        }
    } else if (name === module) {
        var vt = validationTypes[this.validationTypeIndex];
        var chan = { module: module, type: vt.type };

        if (ranges.hasOwnProperty(chan.type)) {
            chan.ranges = ranges[chan.type];
        }

        this.channels.push(chan);
        this.defer(this.doNextValidateStep.bind(this));
    }
};

ControlSpace.prototype.commands.answer = {};
ControlSpace.prototype.commands.answer.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': answer: invalid callId ' + callId);
        return;
    }

    this.answerCall(ci);
    this.sendModuleCommand(null, 'MA', ci.input, indices.tel.answer);
};

ControlSpace.prototype.commands.assistiveListeningMute = {};
ControlSpace.prototype.commands.assistiveListeningMute.get = function() {
    this.doAudioGet('assistiveListeningMute');
};
ControlSpace.prototype.commands.assistiveListeningMute.set = function(value) {
    this.doAudioSet('assistiveListeningMute', value, this.sendMute.bind(this));
};

ControlSpace.prototype.commands.assistiveListeningVolume = {};
ControlSpace.prototype.commands.assistiveListeningVolume.get = function() {
    this.doAudioGet('assistiveListeningVolume');
};
ControlSpace.prototype.commands.assistiveListeningVolume.set = function(value) {
    this.doAudioSet('assistiveListeningVolume', value,
        this.sendVolume.bind(this), this.getRange.bind(this));
};

ControlSpace.prototype.commands.audioInputMute = {};
ControlSpace.prototype.commands.audioInputMute.get = function() {
    this.doAudioGet('audioInputMute');
};
ControlSpace.prototype.commands.audioInputMute.set = function(value) {
    this.doAudioSet('audioInputMute', value, this.sendMute.bind(this));
};

ControlSpace.prototype.commands.audioInputVolume = {};
ControlSpace.prototype.commands.audioInputVolume.get = function() {
    this.doAudioGet('audioInputVolume');
};
ControlSpace.prototype.commands.audioInputVolume.set = function(value) {
    this.doAudioSet('audioInputVolume', value,
        this.sendVolume.bind(this), this.getRange.bind(this));
};

ControlSpace.prototype.commands.call = {};
ControlSpace.prototype.commands.call.set = function(value) {
    var ci = this.getAvailableCallInterface(value.callType);

    if (ci === null) {
        log.error(this + ': no call resources for ' + value.callType);
        return;
    }

    delete ci.callerId; // delete callerId so next poll will cause update

    if (ci.callType === 'pstn') {
        this.connectCall(ci, value.callId, value.dialString);
    } else { // ci.callType === 'voip'
        this.placeCall(ci, value.callId, value.dialString);
    }

    this.sendModuleCommand(null, 'MA', ci.input, indices.tel.call, value.dialString, true);
    // No need to query callerId, it will be queried in onPoll.
};

ControlSpace.prototype.commands.callStatus = {};
ControlSpace.prototype.commands.callStatus.get = function() {
    this.sendCallStatusMessages();
};

ControlSpace.prototype.commands.dial = {};
ControlSpace.prototype.commands.dial.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': dial: invalid callId: ' + value.callId);
        return;
    }

    this.dialCall(ci, value.dialString);

    for (var i = 0; i < value.dialString.length; i++) {
        this.sendModuleCommand(null, 'MA', ci.input, indices.tel.dialKey, value.dialString[i]);
    }
};

ControlSpace.prototype.commands.hangup = {};
ControlSpace.prototype.commands.hangup.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': hangup: invalid callId: ' + callId);
        return;
    }

    this.endCall(ci);
    this.sendModuleCommand(null, 'MA', ci.input, indices.tel.endCall);
};

ControlSpace.prototype.commands.mute = {};
ControlSpace.prototype.commands.mute.get = function() {
    this.doAudioGet('mute');
};
ControlSpace.prototype.commands.mute.set = function(value) {
    this.doAudioSet('mute', value, this.sendMute.bind(this));
};

ControlSpace.prototype.commands.overflowRoomMute = {};
ControlSpace.prototype.commands.overflowRoomMute.get = function() {
    this.doAudioGet('overflowRoomMute');
};
ControlSpace.prototype.commands.overflowRoomMute.set = function(value) {
    this.doAudioSet('overflowRoomMute', value, this.sendMute.bind(this));
};

ControlSpace.prototype.commands.overflowRoomVolume = {};
ControlSpace.prototype.commands.overflowRoomVolume.get = function() {
    this.doAudioGet('overflowRoomVolume');
};
ControlSpace.prototype.commands.overflowRoomVolume.set = function(value) {
    this.doAudioSet('overflowRoomVolume', value,
        this.sendVolume.bind(this), this.getRange.bind(this));
};

ControlSpace.prototype.commands.ping = {};
ControlSpace.prototype.commands.ping.activeStates = [ 'controlling', 'controlFailed' ];
ControlSpace.prototype.commands.ping.get = function() {
    this.sendCommand('ping', 'IP');
};
ControlSpace.prototype.commands.ping.match = /^\x06*IP ([^;]+);*$/;
ControlSpace.prototype.commands.ping.process = function(match) {
    this.broadcastStatus('ping', true);
};

ControlSpace.prototype.commands.receiveGain = {};
ControlSpace.prototype.commands.receiveGain.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': receiveGain: invalid callId: ' + value.callId);
        return;
    }

    this.updateReceiveGain(ci, value.value);
    this.sendModuleCommand(null, 'SA', ci.receiveGain.module, ci.receiveGainIndices, value.value);
};

ControlSpace.prototype.commands.reject = {};
ControlSpace.prototype.commands.reject.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': reject: invalid callId: ' + callId);
        return;
    }

    this.endCall(ci);
    this.sendModuleCommand(null, 'MA', ci.input, indices.tel.endCall);
};

ControlSpace.prototype.commands.soundReinforcementMute = {};
ControlSpace.prototype.commands.soundReinforcementMute.get = function() {
    this.doAudioGet('soundReinforcementMute');
};
ControlSpace.prototype.commands.soundReinforcementMute.set = function(value) {
    this.doAudioSet('soundReinforcementMute', value, this.sendMute.bind(this));
};

ControlSpace.prototype.commands.soundReinforcementVolume = {};
ControlSpace.prototype.commands.soundReinforcementVolume.get = function() {
    this.doAudioGet('soundReinforcementVolume');
};
ControlSpace.prototype.commands.soundReinforcementVolume.set = function(value) {
    this.doAudioSet('soundReinforcementVolume', value,
        this.sendVolume.bind(this), this.getRange.bind(this));
};

ControlSpace.prototype.commands.volume = {};
ControlSpace.prototype.commands.volume.get = function() {
    this.doAudioGet('volume');
};
ControlSpace.prototype.commands.volume.set = function(value) {
    this.doAudioSet('volume', value,
        this.sendVolume.bind(this), this.getRange.bind(this));
};

ControlSpace.prototype.commands.volumeMute = {};
ControlSpace.prototype.commands.volumeMute.get = function() {
    this.doAudioGet('volumeMute');
};

ControlSpace.prototype.computeValidationResults = function() {
    this.validated = {};
    this.valuesToQuery = [];
    this.numNonTelephonyValues = 0;

    this.computeAudioControlValidationResults(this.validated,
        this.configToChannelNames.bind(this),
        this.validateConfig.bind(this),
        this.duplicateAllowed.bind(this)
    );

    if (!this.objIsValid(this.validated)) return;

    this.audioControls.forEach(function(ctrl) {
        if (!ctrl.hasOwnProperty('paths')) return;

        if (ctrl.type === 'mute' || ctrl.type === 'volume') {
            ctrl.paths.forEach(function(path) {
                var cfg = this.getNode(this.config, path.config);
                var chan = this.getChannel(cfg);
                var indices = this.getIndices(ctrl, chan);
                this.valuesToQuery.push({
                    name: chan.module,
                    indices: indices,
                });
            }, this);
        }
    }, this);

    this.callInterfaces.forEach(function(ci) {
        var chan = this.getChannel(ci.input);

        ci.callType = chan.type;

        if (ci.hasOwnProperty('receiveGain')) {
            this.valuesToQuery.push({
                name: ci.receiveGain.module,
                indices: ci.receiveGain.hasOwnProperty('index') ?
                    indices.crr.telReceiveGain(ci.receiveGain.index) :
                    indices.gain.level,
            });
        }
    }, this);

    // Put the telephony state queries at the end and keep track of where they
    // are. At the end of validation, we split these into two arrays so we can
    // poll the telephony state variables quickly while polling everything else
    // more slowly.

    this.numNonTelephonyValues = this.valuesToQuery.length;

    this.callInterfaces.forEach(function(ci) {
        this.valuesToQuery.push({
            name: ci.input,
            indices: indices.tel.callStatus,
        });

        this.valuesToQuery.push({
            name: ci.input,
            indices: indices.tel.callerId,
        });
    }, this);
};

ControlSpace.prototype.configToChannelNames = function(ctrl, cfg) {
    if (ctrl.type === 'telephony') {
        if (cfg.hasOwnProperty('receiveGain')) {
            return [ cfg.input, cfg.receiveGain.module ];
        }

        return [ cfg.input ];
    } else { // 'mute' or 'volume'
        return [ cfg ];
    }
};

var altControls = {
    assistiveListeningMute: 'volume',
    assistiveListeningVolume: 'mute',
    audioInputMute: 'volume',
    audioInputVolume: 'mute',
    overflowRoomMute: 'volume',
    overflowRoomVolume: 'mute',
    soundReinforcementMute: 'volume',
    soundReinforcementVolume: 'mute',
};

ControlSpace.prototype.duplicateAllowed = function(ctrl, path, name, numDuplicates) {
    // First, see if this is the CRR. If it is, we allow it to be duplicated
    // for the room mute and room volume, and also for receive gain as long as
    // the indices are different.

    var chan = this.getChannel(name);

    if (chan.type === 'crr' &&
        (ctrl.cmdName === 'mute' ||
         ctrl.cmdName === 'volume' ||
         ctrl.type === 'telephony')) {
        return true;
    }

    // Finally, allow the duplicate if it is for the same mute/volume control.

    if (!Object.keys(altControls).includes(ctrl.cmdName)) return false;
    if (numDuplicates > 2) return false;

    var altCfgPath = path.config.slice();
    altCfgPath[altCfgPath.length - 1] = altControls[ctrl.cmdName];

    return this.getNode(this.config, path.config) === this.getNode(this.config, altCfgPath);
};

// caller id format: some text <sip:ext@address>
//
// * outgoing calls:
//   * contactName:
//     * if caller id format matches: some text
//     * otherwise, full caller id string
//   * dialString: provided by call command
//   * displayDialString: none
//
// * incoming calls:
//   * contactName:
//     * if caller id format matches: some text
//     * else if caller id not empty: full caller id string
//     * otherwise, none
//   * dialString:
//     * if caller id format matches: sip:ext@address
//     * otherwise, none
//   * displayDialString:
//     * if caler id format matches: ext
//     * otherwise, none

var voipCidRegExp = /^\s*(.+)\s+<sip:(.+)@(.+)>\s*$/;
var pstnCidRegExp = /^(.*)&(.*)&(.*)$/;

ControlSpace.prototype.extractCallerId = function(ci) {
    var result = {};
    var match;

    if (!ci.hasOwnProperty('callStatus')) return null;

    if (ci.callType === 'voip') {
        if (ci.callStatus.direction === 'outgoing') {
            match = voipCidRegExp.exec(ci.callerId);

            if (match) {
                result.contactName = match[1];
                result.dialString = ci.callStatus.dialString;
                result.displayDialString = null;
            } else {
                result.contactName = ci.callerId;
                result.dialString = ci.callStatus.dialString;
                result.displayDialString = null;
            }
        } else { // incoming
            match = voipCidRegExp.exec(ci.callerId);

            if (match) {
                result.contactName = match[1];
                result.dialString = match[2] + '@' + match[3];
                result.displayDialString = match[2];
            } else {
                result.contactName = ci.callerId;
                result.dialString = null;
                result.displayDialString = null;
            }
        }
    } else { // pstn
        match = pstnCidRegExp.exec(ci.callerId);
        var number, name;

        if (match) {
            //time = match[1] === '' ? 'Unknown' : match[1];
            number = match[2] === '' ? 'Unknown' : match[2];
            name = match[3] === '' ? 'Unknown' : match[3];
        } else {
            number = 'Unknown';
            name = 'Unknown';
        }

        if (ci.callStatus.direction === 'outgoing') {
            result.contactName = name;
            result.dialString = ci.callStatus.dialString;
            result.displayDialString = null;
        } else { // incoming
            result.contactName = name;

            if (number === 'Unknown') {
                result.dialString = null;
                result.displayDialString = null;
            } else {
                result.dialString = number;
                result.displayDialString = null;
            }
        }
    }

    return result;
};

ControlSpace.prototype.getCallInterfaceByChannel = function(chan) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.input === chan.module) return ci;
    }

    return null;
};

ControlSpace.prototype.getChannel = function(module) {
    return this.channels.find(function(channel) {
        return channel.module === module;
    }, this);
};

ControlSpace.prototype.getIndices = function(ctrl, chan) {
    if (ctrl.type === 'volume') {
        if (ctrl.subType === 'amplifier') {
            if (chan.type === 'crr') {
                return indices.crr.volume;
            } else if (chan.type === 'gain') {
                return indices.gain.level;
            }
        } else if (ctrl.subType === 'trim') {
            if (chan.type === 'gain') {
                return indices.gain.level;
            }
        }
    } else if (ctrl.type === 'mute') {
        if (chan.type === 'crr') {
            if (ctrl.cmdName === 'volumeMute') {
                return indices.crr.volumeMute;
            } else if (ctrl.cmdName === 'mute') {
                return indices.crr.micsMute;
            }
        } else if (chan.type === 'gain') {
            return indices.gain.mute;
        }
    }

    return null;
};

ControlSpace.prototype.getNextChannel = function() {
    this.validationTypeIndex = 0;
    this.query('$validateChannel');
};

ControlSpace.prototype.getNextValue = function() {
    var query = this.valuesToQuery[this.validateRepeatIndex];
    this.sendModuleCommand('$ga', 'GA', query.name, query.indices);
};

ControlSpace.prototype.getRange = function(ctrl, cfg) {
    var chan = this.getChannel(cfg);

    if (ctrl.subType === 'amplifier') {
        return this.getNode(chan, 'ranges.volume');
    }

    // else ctrl.subType === 'trim'
    return this.getNode(chan, 'ranges.trim');
};

ControlSpace.prototype.handleCallerId = function(chan, callerId) {
    var ci = this.getCallInterfaceByChannel(chan);

    if (ci === null) return;

    if (callerId === '' || callerId === '???') callerId = 'Unknown';
    if (ci.callerId === callerId) return;

    ci.callerId = callerId;
    var cid = this.extractCallerId(ci);
    if (cid === null) return;
    this.updateCallerId(ci, cid.dialString, cid.contactName, cid.displayDialString);
};

ControlSpace.prototype.handleCallStatus = function(chan, value) {
    var ci = this.getCallInterfaceByChannel(chan);

    if (ci === null) return;
    if (ci.callStatusValue === value) return;

    ci.callStatusValue = value;

    switch (ci.callStatusValue) {
        case 'INCOMING':
            delete ci.callerId; // delete caller ID so next poll will cause update
            this.setIncomingCall(ci);
            break;

        case 'DIALING': // only for voip
        case 'OUTGOING':
        case 'RINGBACK': // only for voip
            if (ci.callType === 'pstn') {
                this.connectCall(ci);
            } else {
                this.setOutgoingCall(ci);
            }
            break;

        case 'ACTIVE':
            this.connectCall(ci);
            break;

        case 'HOLD_STATE_LOCAL': // only for voip
        case 'HOLD_STATE_PEER': // only for voip
            log.error(ci.callType + ' module "' + ci.input + '": ' +
                'received call status ' + value +
                ', but hold states are not supported');
            break;

        case 'HANGUP':
        case 'NOT_CONFIGURED': // only for voip
        case 'PROTOCOL': // only for voip
        case 'PEER_NOT_FOUND': // only for voip
        case 'PEER_REJECTED': // only for voip (in email from Rob Brooks 1/23/2017)
        case 'PROXY_TIMEOUT': // only for voip (in email from Rob Brooks 1/23/2017)
        case 'SERVER_FAILURE': // only for voip (in email from Rob Brooks 1/23/2017)
            this.disconnectCall(ci);
            delete ci.callerId;
            break;

        case 'ERROR': // only for pstn
            log.warning(ci.type + ' module "' + ci.input + '" call status ' + value + ' received');
            break;

        default:
            log.error(ci.type + ' module "' + ci.input + '" unknown call status ' + value);
            break;
    }
};

ControlSpace.prototype.indicesMatch = function(index0, index1, indices) {
    if (indices.length === 1 &&
        index0 === indices[0] &&
        typeof index1 === 'undefined' ||
        indices.length === 2 &&
        index0 === indices[0] &&
        index1 === indices[1]) {
        return true;
    }

    return false;
};

ControlSpace.prototype.onComputeCaps = function() {
    var caps = this.getCapsTemplate();
    this.computeCapsFromConfig(caps);
    this.computeCapsMaxCalls(caps);
    caps.hasRoomVolumeMute = caps.hasRoomVolume;
    return caps;
};

ControlSpace.prototype.onPoll = function() {
    // Poll telephony states quickly.

    if (this.telephonyStateQueries.length > 0) {
        this.telephonyStateQueries.forEach(function(query) {
            this.sendModuleCommand('$ga', 'GA', query.name, query.indices);
        }, this);
    }

    // Poll mute and volume states slowly.

    if (this.valuesToQuery.length > 0) {
        var query = this.valuesToQuery[this.pollIndex];
        this.sendModuleCommand('$ga', 'GA', query.name, query.indices);
        if (++this.pollIndex >= this.valuesToQuery.length) this.pollIndex = 0;
    }
};

ControlSpace.prototype.onValidate = function() {
    this.channels.length = 0;
    this.audioControls = this.computeAudioControls();

    this.channelsToValidate = [];
    var module;

    this.audioControls.forEach(function(ctrl) {
        if (!ctrl.hasOwnProperty('paths')) return;

        ctrl.paths.forEach(function(path) {
            if (ctrl.cfgName === 'telephony') {
                var cfg = this.getNode(this.config, path.config);

                module = cfg.input;

                if (!this.channelsToValidate.includes(module)) {
                    this.channelsToValidate.push(module);
                }

                if (cfg.hasOwnProperty('receiveGain')) {
                    module = cfg.receiveGain.module;

                    if (!this.channelsToValidate.includes(module)) {
                        this.channelsToValidate.push(module);
                    }

                    var ci = this.getCallInterfaceByChannel({ module: cfg.input });

                    if (ci === null) {
                        log.error(this + ': onValidate: no call interface for ' + cfg.input);
                        return;
                    }

                    ci.receiveGainIndices = cfg.receiveGain.hasOwnProperty('index') ?
                        indices.crr.telReceiveGain(cfg.receiveGain.index) :
                        indices.gain.level;
                }
            } else {
                module = this.getNode(this.config, path.config);

                if (!this.channelsToValidate.includes(module)) {
                    this.channelsToValidate.push(module);
                }
            }
        }, this);
    }, this);

    this.startValidator();
};

ControlSpace.prototype.sendModuleCommand = function(cmdName, type, modName, indices, value, quote) {
    var cmd = type + '"' + modName + '"';

    for (var i = 0; i < indices.length; i++) {
        cmd += '>' + indices[i];
    }

    if (typeof value !== 'undefined') {
        if (quote) {
            cmd += '="' + value + '"';
        } else {
            cmd += '=' + value;
        }
    }

    this.sendCommand(cmdName, cmd);
};

ControlSpace.prototype.sendMute = function(ctrl, cfg, value) {
    var chan = this.getChannel(cfg);
    var idx = chan.type === 'crr' ?
        ctrl.cmdName === 'volumeMute' ? indices.crr.volumeMute : indices.crr.micsMute :
        indices.gain.mute;

    this.sendModuleCommand(null, 'SA', cfg, idx, value ? 'O' : 'F');
};

ControlSpace.prototype.sendVolume = function(ctrl, cfg, value) {
    var chan = this.getChannel(cfg);
    var idx = chan.type === 'crr' ? indices.crr.volume : indices.gain.level;
    this.sendModuleCommand(null, 'SA', cfg, idx, value);

    if (ctrl.cmdName === 'volume') {
        var newVolumeMute = value <= ranges.crr.volume.min;

        if (newVolumeMute !== this.volumeMute) {
            var volumeMuteCtrl = this.getAudioControl('volumeMute');
            this.volumeMute = newVolumeMute;
            this.broadcastStatus('volumeMute', this.volumeMute);
            this.sendMute(volumeMuteCtrl, this.config.amplifier, this.volumeMute);
        }
    }
};

ControlSpace.prototype.validateConfig = function(ctrl, cfg) {
    var chan;

    if (ctrl.type === 'telephony') {
        chan = this.getChannel(cfg.input);
        if (typeof chan === 'undefined') return 'noChannel';
        if (!getFilter(chan, ctrl.filter)) return 'invalidChannel';
        if (!cfg.hasOwnProperty('receiveGain')) return 'ok';

        chan = this.getChannel(cfg.receiveGain.module);

        if (typeof chan === 'undefined') return 'noChannel';

        if (cfg.receiveGain.hasOwnProperty('index')) {
            if (chan.type !== 'crr') return 'invalidChannel';
        } else {
            if (chan.type !== 'gain') return 'invalidChannel';
        }

        return 'ok';
    }

    // else 'mute' or 'volume'

    chan = this.getChannel(cfg);
    if (typeof chan === 'undefined') return 'noChannel';
    return getFilter(chan, ctrl.filter) ? 'ok' : 'invalidChannel';
};

ControlSpace.prototype.validateSteps = [{
    state: 'gettingChannels',
    action: function() {
        this.defer(this.getNextChannel.bind(this));
        return true;
    },
    getRepeatCount: function() {
        return this.channelsToValidate.length;
    },
}, {
    state: 'computingValidationResults',
    action: function() {
        this.computeValidationResults();
        return false;
    },
}, {
    state: 'gettingValues',
    action: function() {
        this.defer(this.getNextValue.bind(this));
        return true;
    },
    getRepeatCount: function() {
        return this.valuesToQuery.length;
    },
}, {
    state: 'finishingValidation',
    action: function() {
        this.pollIndex = 0;
        this.telephonyStateQueries = this.valuesToQuery.slice(this.numNonTelephonyValues);
        this.valuesToQuery.length = this.numNonTelephonyValues;
        this.finishValidation(this.validated);
        return true;
    },
}];

module.exports = ControlSpace;
